import { z } from 'zod';
import { SystemMessage, systemMessageSchema, generateSystemMessage } from './system';
import { AuthMessage, authMessageSchema, generateAuthMessage } from './auth';
import { ConnectionMessage, connectionMessageSchema, generateConnectionMessage } from './connection';
import { InvitationMessage, invitationMessageSchema, generateInvitationMessage } from './invitation';
import { PresenceMessage, presenceMessageSchema, generatePresenceMessage } from './presence';
import { PeerMessage, peerMessageSchema, generatePeerMessage } from './peer';
import { HostMessage, hostMessageSchema, generateHostMessage } from './host';
import { hostGameSaveStorageMessageSchema } from './gameSaveStorage';
import { LobbyMessage, lobbyMessageSchema, generateLobbyMessage } from './lobby';
import { OutboundMessage, outboundMessageSchema, generateOutboundMessage } from './outbound';
import { WebsocketMessage } from './index.types';
import { generateMetricMessage, MetricsMessage, metricsMessageSchema } from './metrics';
import { ProfileMessage, profileMessageSchema, generateProfileMessage } from './profile';
import { MatchmakingMessage, matchmakingMessageSchema, generateMatchmakingMessage } from './matchmaking';
import { UserEventMessage, userEventMessageSchema, generateUserEventMessage } from './userEvents';
import { AnalyticsMessage, analyticsMessageSchema, generateAnalyticsMessage } from './telemetryMessages';
import { NotificationMessage, notificationMessageSchema, generateNotificationMessage } from './notification';
import { AdminMessage, adminMessageSchema, generateAdminMessage } from './admin';
import { VpnMessage, vpnMessageSchema, generateVpnMessage } from './vpn';
import { BeaconMessage, beaconMessageSchema, generateBeaconMessage } from './beacon';
import {
  GamingScoreMessage,
  GamingScoreMessageType,
  gamingScoreMessageUnion,
  generators as gamingScoreGenerators,
  validators as gamingScoreValidators,
} from './gamingScore';
export {
  gamingScorePersistedDataSchema,
  gamingScoreFullDataSchema,
  submitScoringMetricsPayloadSchema,
  type GamingScorePersistedDataValidated,
} from './gamingScore';
/** Gaming-score namespace: submit raw metrics and receive server-computed scores. */
export const gamingScore = {
  generators: gamingScoreGenerators,
  validators: gamingScoreValidators,
};
import { ProvisioningMessage, provisioningMessageSchema, generateProvisioningMessage } from './provisioning';
import { MacroMessage, macroMessageSchema, generateMacroMessage } from './macroWalk';
import {
  LiveOpsMessage,
  liveOpsMessageUnion,
  generators as liveOpsGenerators,
} from './liveops';
import { UserProfileMessage, userProfileMessageSchema, generateUserProfileMessage } from './userProfile';
import {
  NotificationInboxMessage,
  notificationInboxMessageSchema,
  generateNotificationInboxMessage,
} from './notificationInbox';


// Combined message schema
export const websocketMessageSchema = z.union([
  systemMessageSchema,
  authMessageSchema,
  connectionMessageSchema,
  invitationMessageSchema,
  lobbyMessageSchema,
  metricsMessageSchema,
  peerMessageSchema,
  presenceMessageSchema,
  z.union([hostMessageSchema, hostGameSaveStorageMessageSchema]),
  outboundMessageSchema,
  profileMessageSchema,
  matchmakingMessageSchema,
  userEventMessageSchema,
  analyticsMessageSchema,
  notificationMessageSchema,
  adminMessageSchema,
  vpnMessageSchema,
  beaconMessageSchema,
  gamingScoreMessageUnion,
  provisioningMessageSchema,
  macroMessageSchema,
  liveOpsMessageUnion,
  userProfileMessageSchema,
  notificationInboxMessageSchema,
]);

// Message generators
export const generators = {
  system: generateSystemMessage,
  auth: generateAuthMessage,
  connection: generateConnectionMessage,
  invitation: generateInvitationMessage,
  lobby: generateLobbyMessage,
  metrics: generateMetricMessage,
  peer: generatePeerMessage,
  presence: generatePresenceMessage,
  host: generateHostMessage,
  outbound: generateOutboundMessage,
  profile: generateProfileMessage,
  matchmaking: generateMatchmakingMessage,
  userEvent: generateUserEventMessage,
  analytics: generateAnalyticsMessage,
  notification: generateNotificationMessage,
  admin: generateAdminMessage,
  vpn: generateVpnMessage,
  beacon: generateBeaconMessage,
  gamingScore: gamingScoreGenerators.submitScoringMetrics,
  provisioning: generateProvisioningMessage,
  macro: generateMacroMessage,
  liveOpsEvent: liveOpsGenerators.liveOpsEvent,
  userProfile: generateUserProfileMessage,
  notificationInbox: generateNotificationInboxMessage,
} as const;

// Message validators
export const validators = {
  system: systemMessageSchema,
  auth: authMessageSchema,
  connection: connectionMessageSchema,
  invitation: invitationMessageSchema,
  lobby: lobbyMessageSchema,
  metrics: metricsMessageSchema,
  peer: peerMessageSchema,
  presence: presenceMessageSchema,
  host: z.union([hostMessageSchema, hostGameSaveStorageMessageSchema]),
  outbound: outboundMessageSchema,
  profile: profileMessageSchema,
  matchmaking: matchmakingMessageSchema,
  userEvent: userEventMessageSchema,
  analytics: analyticsMessageSchema,
  notification: notificationMessageSchema,
  admin: adminMessageSchema,
  vpn: vpnMessageSchema,
  beacon: beaconMessageSchema,
  gamingScore: gamingScoreMessageUnion,
  provisioning: provisioningMessageSchema,
  macro: macroMessageSchema,
  liveops: liveOpsMessageUnion,
  userProfile: userProfileMessageSchema,
  notificationInbox: notificationInboxMessageSchema,
} as const;

type ValidationResult = {
  success: boolean;
  error?: z.ZodError;
  data?: WebsocketMessage;
};

// Helper to validate any websocket message
export function validateWebsocketMessage(message: unknown): ValidationResult {
  // try convert to json if string
  try {
    if (typeof message === 'string') message = JSON.parse(message as string);
  } catch (e) {
    return {
      success: false,
      error: new z.ZodError([
        {
          code: z.ZodIssueCode.invalid_type,
          expected: 'object',
          received: typeof message,
          path: [],
          message: 'Message must be a JSON object or string',
        },
      ]),
    };
  }

  if (!message || typeof message !== 'object' || !('type' in message)) {
    return {
      success: false,
      error: new z.ZodError([
        {
          code: z.ZodIssueCode.invalid_type,
          expected: 'object',
          received: typeof message,
          path: ['type'],
          message: 'Message must be an object with a type field',
        },
      ]),
    };
  }

  // Determine message category from type prefix
  const type = (message as { type: string }).type;
  const category = type.split('_')[0];

  // Validate against specific schema first
  switch (category) {
    case 'SYS': {
      const sysResult = validators.system.safeParse(message);
      if (!sysResult.success) return { success: false, error: sysResult.error };
      return { success: true, data: sysResult.data };
    }

    case 'AUTH': {
      const authResult = validators.auth.safeParse(message);
      if (!authResult.success) return { success: false, error: authResult.error };
      return { success: true, data: authResult.data };
    }

    case 'CONN': {
      const connResult = validators.connection.safeParse(message);
      if (!connResult.success) return { success: false, error: connResult.error };
      return { success: true, data: connResult.data };
    }

    case 'INVITATION':
    case 'MACHINE': {
      const invResult = validators.invitation.safeParse(message);
      if (!invResult.success) return { success: false, error: invResult.error };
      return { success: true, data: invResult.data };
    }

    case 'LOBBY': {
      const lobbyResult = validators.lobby.safeParse(message);
      if (!lobbyResult.success) return { success: false, error: lobbyResult.error };
      return { success: true, data: lobbyResult.data };
    }

    case 'METRICS': {
      const metricsResult = validators.metrics.safeParse(message);
      if (!metricsResult.success) return { success: false, error: metricsResult.error };
      return { success: true, data: metricsResult.data };
    }

    case 'PRESENCE': {
      const presResult = validators.presence.safeParse(message);
      if (!presResult.success) return { success: false, error: presResult.error };
      return { success: true, data: presResult.data };
    }

    case 'PEER': {
      const peerResult = validators.peer.safeParse(message);
      if (!peerResult.success) return { success: false, error: peerResult.error };
      return { success: true, data: peerResult.data };
    }

    case 'HOST': {
      const hostResult = validators.host.safeParse(message);
      if (!hostResult.success) return { success: false, error: hostResult.error };
      return { success: true, data: hostResult.data };
    }

    case 'OUTBOUND': {
      const outboundResult = validators.outbound.safeParse(message);
      if (!outboundResult.success) return { success: false, error: outboundResult.error };
      return { success: true, data: outboundResult.data };
    }

    case 'PROFILE': {
      const profileResult = validators.profile.safeParse(message);
      if (!profileResult.success) return { success: false, error: profileResult.error };
      return { success: true, data: profileResult.data };
    }

    case 'MATCHMAKER': {
      const matchmakingResult = validators.matchmaking.safeParse(message);
      if (!matchmakingResult.success) return { success: false, error: matchmakingResult.error };
      return { success: true, data: matchmakingResult.data };
    }

    case 'USEREVENT': {
      const userEventResult = validators.userEvent.safeParse(message);
      if (!userEventResult.success) return { success: false, error: userEventResult.error };
      return { success: true, data: userEventResult.data };
    }

    case 'ANALYTICS': {

      const analyticsResult = validators.analytics.safeParse(message);
      if (!analyticsResult.success) return { success: false, error: analyticsResult.error };
      return { success: true, data: analyticsResult.data as AnalyticsMessage };
    }

    case 'NOTIFICATION': {
      // `NOTIFICATION_INBOX_UPDATED` shares the `NOTIFICATION_` prefix with the
      // SMS/procedural NotificationMessage family, but is a distinct union
      // member. Route it before the notification validator rejects it.
      if (type.startsWith('NOTIFICATION_INBOX_')) {
        const inboxResult = validators.notificationInbox.safeParse(message);
        if (!inboxResult.success) return { success: false, error: inboxResult.error };
        return { success: true, data: inboxResult.data };
      }
      const notificationResult = validators.notification.safeParse(message);
      if (!notificationResult.success) return { success: false, error: notificationResult.error };
      return { success: true, data: notificationResult.data };
    }

    case 'ADMIN': {
      const adminResult = validators.admin.safeParse(message);
      if (!adminResult.success) return { success: false, error: adminResult.error };
      return { success: true, data: adminResult.data };
    }

    case 'VPN': {
      const vpnResult = vpnMessageSchema.safeParse(message);
      if (!vpnResult.success) return { success: false, error: vpnResult.error };
      return { success: true, data: vpnResult.data };
    }

    case 'BEACON': {
      const beaconResult = validators.beacon.safeParse(message);
      if (!beaconResult.success) return { success: false, error: beaconResult.error };
      return { success: true, data: beaconResult.data };
    }

    case 'SUBMIT': {
      const gamingScoreResult = validators.gamingScore.safeParse(message);
      if (!gamingScoreResult.success) return { success: false, error: gamingScoreResult.error };
      return { success: true, data: gamingScoreResult.data };
    }

    case 'PROVISIONING': {
      const provisioningResult = validators.provisioning.safeParse(message);
      if (!provisioningResult.success) return { success: false, error: provisioningResult.error };
      return { success: true, data: provisioningResult.data };
    }

    case 'MACRO': {
      const macroResult = validators.macro.safeParse(message);
      if (!macroResult.success) return { success: false, error: macroResult.error };
      return { success: true, data: macroResult.data };
    }

    case 'LIVEOPS': {
      const liveOpsResult = validators.liveops.safeParse(message);
      if (!liveOpsResult.success) return { success: false, error: liveOpsResult.error };
      return { success: true, data: liveOpsResult.data };
    }

    case 'USER': {
      const userProfileResult = validators.userProfile.safeParse(message);
      if (!userProfileResult.success) return { success: false, error: userProfileResult.error };
      return { success: true, data: userProfileResult.data };
    }

    default:
      return {
        success: false,
        error: new z.ZodError([
          {
            code: z.ZodIssueCode.invalid_enum_value,
            received: category,
            options: ['SYS', 'AUTH', 'CONN', 'INVITATION', 'PRESENCE', 'PEER', 'HOST', 'PROFILE', 'MATCHMAKER', 'USEREVENT', 'ANALYTICS', 'NOTIFICATION', 'ADMIN', 'VPN', 'BEACON', 'SUBMIT', 'PROVISIONING', 'MACRO', 'LIVEOPS', 'USER'],
            path: ['type'],
            message: `Unknown message category: ${category}`,
          },
        ]),
      };
  }
}

export const isSystemMessage = (message: WebsocketMessage): message is SystemMessage => message.type.startsWith('SYS_');
export const isAuthMessage = (message: WebsocketMessage): message is AuthMessage => message.type.startsWith('AUTH_');
export const isConnectionMessage = (message: WebsocketMessage): message is ConnectionMessage => message.type.startsWith('CONN_');
export const isInvitationMessage = (message: WebsocketMessage): message is InvitationMessage => {
  return message.type.startsWith('INVITATION_') || message.type.startsWith('MACHINEHASH_');
};
export const isLobbyMessage = (message: WebsocketMessage): message is LobbyMessage => message.type.startsWith('LOBBY_');
export const isMetricsMessage = (message: WebsocketMessage): message is MetricsMessage => message.type.startsWith('METRICS_');
export const isPresenceMessage = (message: WebsocketMessage): message is PresenceMessage => message.type.startsWith('PRESENCE_');
export const isPeerMessage = (message: WebsocketMessage): message is PeerMessage => message.type.startsWith('PEER_');
export const isHostMessage = (message: WebsocketMessage): message is HostMessage => message.type.startsWith('HOST_');
export const isOutboundMessage = (message: WebsocketMessage): message is OutboundMessage => message.type.startsWith('OUTBOUND_');
export const isProfileMessage = (message: WebsocketMessage): message is ProfileMessage => message.type.startsWith('PROFILE_');
export const isMatchmakingMessage = (message: WebsocketMessage): message is MatchmakingMessage => message.type.startsWith('MATCHMAKER_');
export const isUserEventMessage = (message: WebsocketMessage): message is UserEventMessage => message.type.startsWith('USEREVENT_');
export const isAnalyticsMessage = (message: WebsocketMessage): message is AnalyticsMessage => message.type.startsWith('ANALYTICS_');
export const isNotificationMessage = (message: WebsocketMessage): message is NotificationMessage => message.type.startsWith('NOTIFICATION_');
export const isAdminMessage = (message: WebsocketMessage): message is AdminMessage => message.type.startsWith('ADMIN_');
export const isVpnMessage = (message: WebsocketMessage): message is VpnMessage => message.type.startsWith('VPN_');
export const isBeaconMessage = (message: WebsocketMessage): message is BeaconMessage => message.type.startsWith('BEACON_');
export const isGamingScoreMessage = (message: WebsocketMessage): message is GamingScoreMessage =>
  (Object.values(GamingScoreMessageType) as string[]).includes(message.type);
export const isProvisioningMessage = (message: WebsocketMessage): message is ProvisioningMessage => message.type.startsWith('PROVISIONING_');
export const isMacroMessage = (message: WebsocketMessage): message is MacroMessage => message.type.startsWith('MACRO_');
export const isLiveOpsMessage = (message: WebsocketMessage): message is LiveOpsMessage => message.type.startsWith('LIVEOPS_');
export const isUserProfileMessage = (message: WebsocketMessage): message is UserProfileMessage => message.type.startsWith('USER_PROFILE_');
export const isNotificationInboxMessage = (message: WebsocketMessage): message is NotificationInboxMessage =>
  message.type.startsWith('NOTIFICATION_INBOX_');
